import React, { forwardRef, useState, useEffect, useRef } from 'react';
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  Button,
  IconButton,
  Textarea,
  Tooltip,
  Image,
  List,
  ListItem,
} from '@chakra-ui/react';
import gallery_icon from '../Assets/icons/gallery_icon.svg';
import car_icon from '../Assets/icons/speaker-two.svg';
import send_icon from '../Assets/icons/send-icon.svg';
import sidebar_icon from '../Assets/icons/sidebar_icon.svg';
import { FaCar, FaWrench, FaWarehouse, FaHeadset } from 'react-icons/fa';
import WaveformAudioPlayer from './WaveformAudioPlayer';
import { useUIStore } from '../store/uiStore';

const ChatInputSection = forwardRef(({
  isMobile,
  isLoggedIn,
  // Button states
  isDiagnoseMode,
  isBuySellMode,
  // Button handlers
  onDiagnoseRepairVehicle,
  onBuySell,
  onGoToGarage,
  onHelpExpert,
  goToGarageLabel = 'Go to Garage',
  // Input states
  newMessage,
  selectedImage,
  selectedAudio,
  isLoading,
  // Input handlers
  onMessageChange,
  onKeyPress,
  onSendMessage,
  onImageButtonClick,
  onCarIconClick,
  onSidebarToggle,
  onRemoveImage,
  onRemoveAudio,
  // Refs
  fileInputRef,
  audioInputRef,
  textareaRef,
  // Image/Audio preview handlers
  handleImageSelect,
  handleAudioSelect,
  // Suggestions for autocomplete
  suggestions = [],
}, ref) => {
  const { isSidebarOpen } = useUIStore();
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestionsRef = useRef(null);
  const inputWrapperRef = useRef(null);
  const sidebarOffset = !isMobile && isSidebarOpen ? '300px' : '0px';
  const horizontalPadding = isMobile ? 3 : isSidebarOpen ? 6 : 10;
  const verticalPadding = isMobile ? 3 : 4;
  const desktopMaxWidth = !isMobile
    ? isSidebarOpen
      ? 'calc(100vw - 360px)'
      : '1200px'
    : 'full';
  const desktopMarginX = !isMobile ? (isSidebarOpen ? undefined : 'auto') : undefined;

  // Extract prompt text from suggestions (handle different response formats)
  const getSuggestionText = (suggestion) => {
    if (typeof suggestion === 'string') return suggestion;
    if (suggestion?.prompt) return suggestion.prompt;
    if (suggestion?.text) return suggestion.text;
    if (suggestion?.suggestion) return suggestion.suggestion;
    return String(suggestion);
  };

  // Filter suggestions based on input
  useEffect(() => {
    if (!newMessage.trim() || !suggestions || suggestions.length === 0) {
      setFilteredSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const inputLower = newMessage.toLowerCase().trim();
    const characterCount = inputLower.length;
    
    // If user has typed less than 5 characters, show all suggestions (up to 10)
    // If user has typed 5 or more characters, only show matching suggestions
    let filtered;
    if (characterCount < 5) {
      // Show all suggestions (up to 10) when typing less than 5 characters
      filtered = suggestions
        .map(getSuggestionText)
        .slice(0, 10);
    } else {
      // After 5 characters, only show matching suggestions
      filtered = suggestions
        .map(getSuggestionText)
        .filter(suggestion => 
          suggestion.toLowerCase().includes(inputLower) && 
          suggestion.toLowerCase() !== inputLower
        )
        .slice(0, 10);
    }

    setFilteredSuggestions(filtered);
    // Show suggestions if there are any to show
    setShowSuggestions(filtered.length > 0);
    setSelectedIndex(-1);
  }, [newMessage, suggestions]);

  // Handle clicking outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        inputWrapperRef.current &&
        !inputWrapperRef.current.contains(event.target) &&
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    if (showSuggestions) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showSuggestions]);

  // Handle suggestion selection
  const handleSuggestionSelect = (suggestion) => {
    const suggestionText = getSuggestionText(suggestion);
    // Create a synthetic event to update the message
    const syntheticEvent = {
      target: { value: suggestionText },
      preventDefault: () => {},
    };
    onMessageChange(syntheticEvent);
    setShowSuggestions(false);
    setSelectedIndex(-1);
    // Focus back on textarea
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  // Enhanced key press handler for autocomplete
  const handleKeyPressWithAutocomplete = (e) => {
    if (showSuggestions && filteredSuggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < filteredSuggestions.length - 1 ? prev + 1 : prev
        );
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        return;
      }
      if (e.key === 'Enter' && selectedIndex >= 0) {
        e.preventDefault();
        handleSuggestionSelect(filteredSuggestions[selectedIndex]);
        return;
      }
      if (e.key === 'Escape') {
        setShowSuggestions(false);
        setSelectedIndex(-1);
        return;
      }
    }
    // Call original onKeyPress for other keys
    onKeyPress(e);
  };

  return (
    <Box
      ref={ref}
      position="fixed"
      bottom={0}
      left={isMobile ? 0 : sidebarOffset}
      right={0}
      bg="#F8F8F8"
      px={horizontalPadding}
      py={verticalPadding}
      borderTop="1px"
      borderTopColor={isMobile ? "#C3C3C3" : "gray.200"}
      w="full"
      borderTopRadius="xl"
      borderLeftRadius="xl"
      borderRightRadius="xl"
      zIndex={10}
      transition="left 0.2s ease, padding 0.2s ease"
    >
      {/* Image Preview */}
      {selectedImage && (
        <Box 
          mb={3} 
          p={3} 
          bg="white" 
          borderRadius="lg" 
          border="1px" 
          borderColor="blue.200"
          maxW={desktopMaxWidth}
          mx={desktopMarginX}
          ml={!isMobile && isSidebarOpen ? 2 : undefined}
        >
          <Text fontSize="sm" fontWeight="medium" color="blue.700" mb={2}>
            Selected Image:
          </Text>
          <Box position="relative" display="inline-block">
            <Image
              src={URL.createObjectURL(selectedImage)}
              alt="Preview"
              maxH="100px"
              borderRadius="md"
              objectFit="cover"
            />
            <IconButton
              size="xs"
              variant="ghost"
              color="red.500"
              onClick={onRemoveImage}
              aria-label="Remove image"
              icon={<Text fontSize="sm">×</Text>}
              position="absolute"
              top="4px"
              right="4px"
              bg="white"
              borderRadius="full"
              boxShadow="sm"
              _hover={{ bg: "red.50" }}
            />
          </Box>
          <Text fontSize="xs" color="gray.600" mt={1} noOfLines={1}>
            {selectedImage.name} ({(selectedImage.size / 1024 / 1024).toFixed(2)} MB)
          </Text>
        </Box>
      )}

      {/* Audio Preview */}
      {selectedAudio && (
        <Box 
          mb={3} 
          p={3} 
          bg="white" 
          borderRadius="lg" 
          border="1px" 
          borderColor="blue.200"
          maxW={desktopMaxWidth}
          mx={desktopMarginX}
          ml={!isMobile && isSidebarOpen ? 2 : undefined}
        >
          <Flex align="center" justify="space-between" mb={2} minH="24px">
            <Text fontSize="sm" fontWeight="medium" color="blue.700" flex="1" mr={2} noOfLines={1}>
              🔊 Selected Audio:
            </Text>
            <IconButton
              size="xs"
              variant="ghost"
              color="red.500"
              onClick={onRemoveAudio}
              aria-label="Remove audio"
              icon={<Text fontSize="sm">×</Text>}
              flexShrink={0}
              minW="24px"
              minH="24px"
            />
          </Flex>
          <WaveformAudioPlayer
            src={URL.createObjectURL(selectedAudio)}
            type={selectedAudio.type}
            isMobile={isMobile}
          />
          <Text fontSize="xs" color="gray.600" mt={1} noOfLines={1} wordBreak="break-all">
            {selectedAudio.name} ({(selectedAudio.size / 1024 / 1024).toFixed(2)} MB)
          </Text>
        </Box>
      )}

      {/* Process Management Buttons - Only show when logged in */}
      {isLoggedIn && (
        isMobile ? (
          <VStack spacing={2} align="stretch" mb={2}>
            <HStack spacing={2} wrap="wrap">
              <Button
                variant="ghost"
                size="sm"
                color={isDiagnoseMode ? "white" : "#212e89"}
                bg={isDiagnoseMode ? "#212e89" : "#E8E8F0"}
                border={isDiagnoseMode ? "2px solid" : "2px solid"}
                borderColor="#212e89"
                _hover={{
                  color: 'white',
                  bg: '#212e89'
                }}
                onClick={onDiagnoseRepairVehicle}
                fontSize="sm"
                fontWeight="medium"
                flex="1"
                minW="120px"
              >
                <HStack spacing={1.5}>
                  <Box as={FaCar} color="currentColor" fontSize="14px" />
                  <Text>{isDiagnoseMode ? '✓ Diagnose Repair vehicle' : 'Diagnose Repair vehicle'}</Text>
                </HStack>
              </Button>

              <Button
                variant="ghost"
                size="sm"
                color={isBuySellMode ? "white" : "#212e89"}
                bg={isBuySellMode ? "#212e89" : "#E8E8F0"}
                border="2px solid"
                borderColor="#212e89"
                _hover={{
                  color: 'white',
                  bg: '#212e89'
                }}
                onClick={onBuySell}
                fontSize="sm"
                fontWeight="medium"
                flex="1"
                minW="120px"
              >
                <HStack spacing={1.5}>
                  <Box as={FaWrench} color="currentColor" fontSize="14px" />
                  <Text>{isBuySellMode ? '✓ Vehicle Value' : 'Vehicle Value'}</Text>
                </HStack>
              </Button>
            </HStack>
            <HStack spacing={2} wrap="wrap">
              <Button
                variant="ghost"
                size="sm"
                color="#212e89"
                bg="#E8E8F0"
                border="2px solid"
                borderColor="#212e89"
                _hover={{
                  color: 'white',
                  bg: '#212e89'
                }}
                onClick={onGoToGarage}
                fontSize="sm"
                fontWeight="medium"
                flex="1"
                minW="120px"
              >
                <HStack spacing={1.5}>
                  <Box as={FaWarehouse} color="currentColor" fontSize="14px" />
                  <Text>{goToGarageLabel}</Text>
                </HStack>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                color="#212e89"
                bg="#E8E8F0"
                border="2px solid"
                borderColor="#212e89"
                _hover={{
                  color: 'white',
                  bg: '#212e89'
                }}
                onClick={onHelpExpert}
                fontSize="sm"
                fontWeight="medium"
                flex="1"
                minW="120px"
              >
                <HStack spacing={1.5}>
                  <Box as={FaHeadset} color="currentColor" fontSize="14px" />
                  <Text>Help Expert</Text>
                </HStack>
              </Button>
            </HStack>
          </VStack>
        ) : (
          <HStack
            justify={isSidebarOpen ? "flex-start" : "center"}
            mb={3}
            maxW={desktopMaxWidth}
            mx={desktopMarginX}
            // ml={isSidebarOpen ? 2 : 0}
            spacing={2}
            wrap="wrap"
          >
            <Button
              variant="ghost"
              size="md"
              color={isDiagnoseMode ? "white" : "#212e89"}
              bg={isDiagnoseMode ? "#212e89" : "#E8E8F0"}
              border="2px solid"
              borderColor="#212e89"
              _hover={{
                color: 'white',
                bg: '#212e89'
              }}
              onClick={onDiagnoseRepairVehicle}
              fontSize="sm"
              fontWeight="medium"
              flex="1"
              minW="120px"
            >
              <HStack spacing={1.5}>
                <Box as={FaCar} color="currentColor" fontSize="14px" />
                <Text>{isDiagnoseMode ? '✓ Diagnose Repair Vehicle' : 'Diagnose Repair Vehicle'}</Text>
              </HStack>
            </Button>
            <Button
              variant="ghost"
              size="md"
              color={isBuySellMode ? "white" : "#212e89"}
              bg={isBuySellMode ? "#212e89" : "#E8E8F0"}
              border="2px solid"
              borderColor="#212e89"
              _hover={{
                color: 'white',
                bg: '#212e89'
              }}
              onClick={onBuySell}
              fontSize="sm"
              fontWeight="medium"
              flex="1"
              minW="120px"
            >
              <HStack spacing={1.5}>
                <Box as={FaWrench} color="currentColor" fontSize="14px" />
                <Text>{isBuySellMode ? '✓ Vehicle Value' : 'Vehicle Value'}</Text>
              </HStack>
            </Button>
            <Button
              variant="ghost"
              size="md"
              color="#212e89"
              bg="#E8E8F0"
              border="2px solid"
              borderColor="#212e89"
              _hover={{
                color: 'white',
                bg: '#212e89'
              }}
              onClick={onGoToGarage}
              fontSize="sm"
              fontWeight="medium"
              flex="1"
              minW="120px"
            >
              <HStack spacing={1.5}>
                <Box as={FaWarehouse} color="currentColor" fontSize="14px" />
                <Text>{goToGarageLabel}</Text>
              </HStack>
            </Button>
            <Button
              variant="ghost"
              size="md"
              color="#212e89"
              bg="#E8E8F0"
              border="2px solid"
              borderColor="#212e89"
              _hover={{
                color: 'white',
                bg: '#212e89'
              }}
              onClick={onHelpExpert}
              fontSize="sm"
              fontWeight="medium"
              flex="1"
              minW="120px"
            >
              <HStack spacing={1.5}>
                <Box as={FaHeadset} color="currentColor" fontSize="14px" />
                <Text>Help Expert</Text>
              </HStack>
            </Button>
          </HStack>
        )
      )}

      <form onSubmit={onSendMessage}>
        <Box
          position="relative"
          maxW={desktopMaxWidth}
          mx={desktopMarginX}
          ml={!isMobile && isSidebarOpen ? 2 : undefined}
          ref={inputWrapperRef}
        >
          <HStack
            spacing={isMobile ? 3 : 2}
            pl={!isMobile ? 0 : undefined}
          >
          {/* Icon buttons - Only show when logged in */}
          {isLoggedIn && (
            <>
              <Tooltip label="Upload a photo for analysis – Supported: JPG, JPEG, PNG (max 100MB)" placement="top" hasArrow>
                <IconButton
                  icon={<Image src={gallery_icon} alt="Gallery" w={isMobile ? "4" : "5"} h={isMobile ? "4" : "5"} />}
                  variant="ghost"
                  size={isMobile ? "sm" : "md"}
                  color="gray.400"
                  _hover={{ color: 'gray.600' }}
                  aria-label="Image"
                  onClick={onImageButtonClick}
                  isDisabled={isLoading}
                  ml={!isMobile ? 0 : undefined}
                />
              </Tooltip>
              <input
                type="file"
                accept=".jpg,.jpeg,.png"
                ref={fileInputRef}
                onChange={handleImageSelect}
                style={{ display: 'none' }}
              />
              <input
                type="file"
                accept="audio/*"
                ref={audioInputRef}
                onChange={handleAudioSelect}
                style={{ display: 'none' }}
              />
              <Tooltip label="Upload Audio – Max 100MB" placement="top" hasArrow>
                <IconButton
                  icon={<Image src={car_icon} alt="Car" w={isMobile ? "4" : "5"} h={isMobile ? "4" : "5"} />}
                  variant="ghost"
                  size={isMobile ? "sm" : "md"}
                  color="gray.400"
                  _hover={{ color: 'gray.600' }}
                  aria-label="Car"
                  onClick={onCarIconClick}
                  isDisabled={isLoading}
                />
              </Tooltip>
              <Tooltip label="Toggle Sidebar" placement="top" hasArrow>
                <IconButton
                  icon={<Image src={sidebar_icon} alt="Sidebar" w={isMobile ? "4" : "5"} h={isMobile ? "4" : "5"} />}
                  variant="ghost"
                  size={isMobile ? "sm" : "md"}
                  color="gray.400"
                  _hover={{ color: 'gray.600' }}
                  aria-label="Toggle Sidebar"
                  onClick={onSidebarToggle}
                  isDisabled={isLoading}
                />
              </Tooltip>
            </>
          )}
          <Box position="relative" flex={1}>
            <Textarea
              placeholder={isLoading ? "AI is responding..." : selectedImage ? `Ask about: ${selectedImage.name} (Press Enter to send)` : selectedAudio ? `Audio selected: ${selectedAudio.name} (Press Enter to send)` : "Describe your vehicle issue"}
              value={newMessage}
              onChange={onMessageChange}
              onKeyPress={handleKeyPressWithAutocomplete}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Escape') {
                  handleKeyPressWithAutocomplete(e);
                }
              }}
              bg="white"
              border="none"
              borderRadius={isMobile ? "lg" : "xl"}
              _focus={{ boxShadow: 'none' }}
              isDisabled={isLoading}
              borderColor={selectedImage ? "blue.300" : "transparent"}
              borderWidth={selectedImage ? "2px" : "0px"}
              resize="none"
              minH="60px"
              fontSize={isMobile ? "sm" : "md"}
              py={isMobile ? 2 : 4}
              px={isMobile ? 3 : 4}
              ref={textareaRef}
              overflow="hidden"
              lineHeight="1.4"
            />
            {/* Suggestions Dropdown */}
            {showSuggestions && filteredSuggestions.length > 0 && (
              <Box
                ref={suggestionsRef}
                position="absolute"
                bottom="100%"
                left={isMobile ? "-135px" : 0}
                right={isMobile ? "-50px" : 0}
                mb={2}
                bg="white"
                borderRadius="lg"
                boxShadow="lg"
                border="1px"
                borderColor="black"
                maxH="200px"
                overflowY="auto"
                zIndex={1000}
              >
                <List spacing={0}>
                  {filteredSuggestions.map((suggestion, index) => {
                    const suggestionText = getSuggestionText(suggestion);
                    return (
                      <ListItem
                        key={index}
                        px={4}
                        py={2}
                        cursor="pointer"
                        bg={selectedIndex === index ? "blue.50" : "white"}
                        _hover={{ bg: "blue.50" }}
                        onClick={() => handleSuggestionSelect(suggestion)}
                        borderBottom={index < filteredSuggestions.length - 1 ? "1px" : "none"}
                        borderColor="gray.100"
                      >
                        <Text fontSize={isMobile ? "sm" : "md"} color="gray.700">
                          {suggestionText}
                        </Text>
                      </ListItem>
                    );
                  })}
                </List>
              </Box>
            )}
          </Box>
          <IconButton
            icon={<Image src={send_icon} alt="Send" w={isMobile ? "4" : "6"} h={isMobile ? "4" : "6"} />}
            variant={isMobile ? "ghost" : "solid"}
            size={isMobile ? "sm" : "md"}
            color={isMobile ? "blue.500" : undefined}
            _hover={isMobile ? { color: 'blue.600', bg: 'blue.50' } : undefined}
            aria-label="Send message"
            onClick={onSendMessage}
            isDisabled={isLoading || (!newMessage.trim() && !selectedAudio && !selectedImage)}
            type="submit"
          />
          </HStack>
        </Box>
      </form>
    </Box>
  );
});

ChatInputSection.displayName = 'ChatInputSection';

export default ChatInputSection;

